// Home prefetch (L1 boot prefetch + L4 image preloads).
// Fires the four homepage data requests at BUNDLE EVALUATION on "/" — before
// React mounts — using the market guessed from persisted user settings (exact
// from visit #2; first visit falls back to the US/USD default). Requests are
// strictly GLOBAL mode (denied consent header, no visitor identifiers) — a
// consent decision cannot be pending when prefetch runs, so no personal signal
// may egress. useStorefront consumes a prefetched response only when its own
// request mode matches (global + same market); personalized sessions always
// fetch fresh, exactly as before. A wrong guess costs one wasted cache read.
import API from "./API";

const REG_TTL_MS = 30000; // prefetch is meaningless once the SWR pool may have turned
const registry = new Map(); // `${kind}|${country}|${currency}` → { promise, data, at, consumed }
let started = false;

export function guessMarket() {
  try {
    const raw = window.localStorage.getItem("user_settings");
    if (raw) {
      const parsed = JSON.parse(raw);
      const country = String(parsed?.shipToCountry || "").toUpperCase().slice(0, 2);
      const currency = String(parsed?.currency || "").toUpperCase().slice(0, 3);
      if (/^[A-Z]{2}$/.test(country) && /^[A-Z]{3}$/.test(currency)) return { country, currency };
    }
  } catch {
    /* private mode / corrupt JSON → default market */
  }
  return { country: "US", currency: "USD" };
}

export const PREFETCH_KINDS = [
  { key: "hero", endpoint: "/product/hero", params: { limit: 12, includeCampaign: false } },
  { key: "trending", endpoint: "/product/trending", params: { limit: 12 } },
  { key: "collections", endpoint: "/product/collections", params: { limit: 3 } },
  { key: "rows", endpoint: "/product/", params: { page: 1, limit: 12, paginationMode: "cursor" } },
];

function preloadImages(endpoint, list) {
  if (typeof document === "undefined") return;
  const urls = (Array.isArray(list) ? list : [])
    .map((p) => p?.images?.[0] || p?.image)
    .filter((u) => typeof u === "string" && /^https?:\/\//.test(u))
    .slice(0, endpoint === "/product/hero" ? 3 : 2);
  for (const url of urls) {
    if (document.head.querySelector(`link[data-prefetch-img="${url}"]`)) continue;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = url;
    link.setAttribute("data-prefetch-img", url);
    document.head.appendChild(link);
  }
}

export function startHomePrefetch() {
  if (started || typeof window === "undefined") return;
  if (window.location.pathname !== "/") return;
  started = true;
  const { country, currency } = guessMarket();
  for (const spec of PREFETCH_KINDS) {
    const regKey = `${spec.key}|${country}|${currency}`;
    const promise = API.get(spec.endpoint, {
      params: { country, currency, ...spec.params },
      headers: { "x-personalization-consent": "denied", Authorization: "" },
      timeout: 15000,
    })
      .then(({ data }) => {
        const entry = registry.get(regKey);
        if (entry) {
          entry.data = data;
          entry.at = Date.now();
        }
        preloadImages(spec.endpoint, data?.data);
        return data;
      })
      .catch(() => {
        registry.delete(regKey);
        return null;
      });
    registry.set(regKey, { promise, data: null, at: Date.now(), consumed: false });
  }
}

// Returns a promise of the prefetched payload, or null when unusable
// (absent, expired, already consumed, or fetch still unsettled-and-failing).
export function consumePrefetch(kind, country, currency) {
  const regKey = `${kind}|${country}|${currency}`;
  const entry = registry.get(regKey);
  if (!entry || entry.consumed) return null;
  if (Date.now() - entry.at > REG_TTL_MS) {
    registry.delete(regKey);
    return null;
  }
  entry.consumed = true;
  if (entry.data) {
    registry.delete(regKey);
    return Promise.resolve(entry.data);
  }
  return entry.promise.then((data) => {
    registry.delete(regKey);
    return data;
  });
}

export function resetHomePrefetchForTesting() {
  registry.clear();
  started = false;
}

// Fired from src/index.js (app entry) — NOT at module evaluation — so test
// imports of this module never trigger network prefetches.
